import React, { useEffect, useCallback, lazy, Suspense } from 'react';
import { useDispatch, useSelector } from '../hooks';
import { Router, Switch, Route, Redirect } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import { Alert, Loading, Nav } from '../components/kit';
import history from '../utils/history';
import { setToken } from '../redux/modules/token';
import { actions as sessionActions } from '../redux/modules/session';
import { actions as userSiteActions } from '../redux/modules/site/userSite';
import { actions as userSiteInfoActions } from '../redux/modules/site/userSiteInfo';
import {
  isEmpty,
  loadAsyncAction,
  __get,
  getFilePathForRouter,
} from '../utils';
import RegisterRoute from './RegisterRoute';
import Routes from './Routes';

export default function Navigator() {
  const dispatch = useDispatch();
  const session = useSelector('session');
  const profile = useSelector('profile');
  const userSite = useSelector('userSite');
  const userSiteInfo = useSelector('userSiteInfo');
  const templateId = userSite?.data?.template?._id;

  useEffect(() => {
    console.log(
      `%c env:${process.env.NODE_ENV}`,
      'font-size: 11px; color: #d22862'
    );

    const token = __get(session, 'data.session.token');
    if (token) dispatch(setToken(token, true));
    else dispatch(sessionActions.load());

    asyncDispatch(userSiteActions).then(({ _id }) => {
      dispatch(userSiteInfoActions.load({ siteId: _id }));
    });
  }, []);

  const isLoggedIn = isEmpty(profile.error) && !isEmpty(profile.data);

  // making a custom dispatch that will return a promise which we can wait for.
  const asyncDispatch = useCallback(
    (action, args = {}) => loadAsyncAction(dispatch, action, args),
    [dispatch]
  );

  return (
    <Router history={history}>
      <Alert />
      <Suspense fallback={<Loading fullScreen />}>
        {(session.loading && !session.loaded) ||
        (profile.loading && !profile.loaded) ||
        (userSite.loading && !userSite.loaded) ||
        (userSiteInfo.loading && !userSiteInfo.loaded) ? (
          <Loading fullScreen />
        ) : null}
        {!session.loading &&
        session.loaded &&
        !userSite.loading &&
        userSite.loaded &&
        !profile.loading &&
        profile.loaded ? (
          <Switch>
            {Routes.RegisterRoutes.map(({ container }) => (
              <RegisterRoute
                key={container}
                exact
                path={'/' + container}
                component={lazy(() => import(`../containers/${container}`))}
                authenticated={isLoggedIn}
              />
            ))}
            {Routes.PrivateRoutes.map(({ path, container, templates }) => {
              const filePath = getFilePathForRouter(container);
              return (
                <PrivateRoute
                  key={container}
                  exact
                  templates={templates}
                  template={templateId}
                  path={'/' + container}
                  render={({ history, match, location, ...rest }) => {
                    const Container = lazy(() =>
                      import(`../containers/${filePath}`)
                    );
                    return (
                      <Nav route={match.path} template={templateId}>
                        <Container
                          asyncDispatch={asyncDispatch}
                          history={history}
                          match={match}
                          {...rest}
                        />
                      </Nav>
                    );
                  }}
                  authenticated={isLoggedIn}
                />
              );
            })}
            <Route
              render={() => (
                <Redirect to={{ pathname: isLoggedIn ? '/' : '/auth/login' }} />
              )}
            />
          </Switch>
        ) : (
          <Loading fullScreen />
        )}
      </Suspense>
    </Router>
  );
}
